import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TEACHER_SCENARIO_CATEGORIES,
  type TeacherScenarioCategory,
} from "@/config/teacherScenarios";
import type { CustomScenarioInput } from "@/services/customScenarioService";

interface ScenarioBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCategory?: TeacherScenarioCategory;
  onCreate: (input: CustomScenarioInput) => void;
}

const emptyForm = (category: TeacherScenarioCategory): CustomScenarioInput => ({
  title: "",
  category,
  summary: "",
  openingPrompt: "",
  coachingFocus: "",
  twist: "",
});

export const ScenarioBuilderDialog = ({
  open,
  onOpenChange,
  defaultCategory = "student_behavior",
  onCreate,
}: ScenarioBuilderDialogProps) => {
  const [form, setForm] = useState<CustomScenarioInput>(emptyForm(defaultCategory));

  useEffect(() => {
    if (open) setForm(emptyForm(defaultCategory));
  }, [open, defaultCategory]);

  const canSubmit =
    form.title.trim().length > 1 &&
    form.summary.trim().length > 1 &&
    form.openingPrompt.trim().length > 5;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onCreate({
      ...form,
      title: form.title.trim(),
      summary: form.summary.trim(),
      openingPrompt: form.openingPrompt.trim(),
      coachingFocus: form.coachingFocus?.trim() || undefined,
      twist: form.twist?.trim() || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-literata text-bodhi-teal">
            <Sparkles className="h-5 w-5" />
            Create your own scenario
          </DialogTitle>
          <DialogDescription>
            Describe a real situation you want to practice. HeyBodhi will coach you on it just like the built-in scenarios.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="scenario-title">Title</Label>
            <Input
              id="scenario-title"
              value={form.title}
              maxLength={80}
              placeholder="e.g. Parent upset about seating chart"
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scenario-category">Category</Label>
            <Select
              value={form.category}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, category: value as TeacherScenarioCategory }))
              }
            >
              <SelectTrigger id="scenario-category">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent>
                {TEACHER_SCENARIO_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scenario-summary">Why this matters (one line)</Label>
            <Input
              id="scenario-summary"
              value={form.summary}
              maxLength={160}
              placeholder="e.g. I freeze when a parent challenges my decisions."
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scenario-situation">The situation</Label>
            <Textarea
              id="scenario-situation"
              value={form.openingPrompt}
              rows={3}
              maxLength={600}
              placeholder="Describe the moment as if setting the scene: who's involved, what just happened, and what makes it hard."
              onChange={(e) => setForm((f) => ({ ...f, openingPrompt: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scenario-focus">
              What do you want to get better at? <span className="text-gray-400">(optional)</span>
            </Label>
            <Input
              id="scenario-focus"
              value={form.coachingFocus}
              maxLength={160}
              placeholder="e.g. Staying calm and not over-explaining."
              onChange={(e) => setForm((f) => ({ ...f, coachingFocus: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scenario-twist">
              A possible twist for Practice mode <span className="text-gray-400">(optional)</span>
            </Label>
            <Input
              id="scenario-twist"
              value={form.twist}
              maxLength={200}
              placeholder="e.g. Mid-conversation, they threaten to call the principal."
              onChange={(e) => setForm((f) => ({ ...f, twist: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="bg-bodhi-teal font-inter hover:bg-bodhi-teal/90"
          >
            Save scenario
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
